import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { defaultPurchasingQuantity } from '@models/util/product.util'
import { useAPIError } from '@providers/APIErrorProvider'
import { usePageApi } from '@providers/PageApiProvider'
import { useTitle } from '@providers/TitleProvider'
import CommerceService from '@services/CommerceService'
import TitleService from '@services/TitleService'
import { useDialogStore } from '@stores/dialog.store'
import { useSession } from '@stores/globalData/globalData.store'
import GoogleTagManager from '@utils/gtm/google-tag-manager'
import PageRoutes from '@utils/page-routes'

import usePurchasePrice from './usePurchasePrice'

import type { PaymentFormRefType } from '../../../PaymentForm/PaymentForm'

export enum DialogState {
  SIGN_IN_REQUIRED = 'sign_in_required',
  READY = 'ready',
  SUBMITTED = 'submitted',
  ERROR = 'error',
  COMPLETED = 'completed',
}

export interface TaxApiParams {
  route: string
  params: {
    product_id: string
    price: number
    source_id: string
  }
}

function refreshEmbed(): void {
  const embedEl = document.getElementById('embed_score') as HTMLIFrameElement | null
  if (embedEl?.contentWindow) {
    embedEl.contentWindow.location.reload()
  }
}

const usePurchaseTitleDialog = ({ initialAuthorizedPerformers }) => {
  const { t } = useTranslation()
  const openDialog = useDialogStore((s) => s.openDialog)
  const closeDialog = useDialogStore((s) => s.closeDialog)

  const navigate = useNavigate()
  const session = useSession()
  const { refetchPageData } = usePageApi()

  const { clearErrors } = useAPIError()

  const { title, updateTitle } = useTitle()
  const [uiState, setUiState] = useState<DialogState>(
    session.isLoggedIn() ? DialogState.READY : DialogState.SIGN_IN_REQUIRED
  )

  const [dialogErrorCode, setDialogErrorCode] = useState<string | null>()
  const [selectedAuthorizedPerformers, setSelectedAuthorizedPerformers] = useState(
    Math.max(initialAuthorizedPerformers, defaultPurchasingQuantity(title))
  )
  const [isValid, setIsValid] = useState(false)
  const [taxApiParams, setTaxApiParams] = useState<TaxApiParams | undefined>()
  const [idempotencyKey, setIdempotencyKey] = useState()

  const paymentFormRef = useRef<PaymentFormRefType | null>(null)

  // if a user already owns a quantity of 10 authorized performers
  // and selects 11 authorized performers on a subsequent purchase
  // the purchaseQuantity is 1
  const purchaseQuantity = useMemo(() => {
    const purchasedQuantity = title?.purchased_product?.purchased_quantity
    if (purchasedQuantity) {
      return selectedAuthorizedPerformers - purchasedQuantity
    }
    return selectedAuthorizedPerformers
  }, [selectedAuthorizedPerformers, title.purchased_product?.purchased_quantity])

  const { price, clientSecret } = usePurchasePrice({
    taxApiParams,
    product: title.product!,
    quantity: purchaseQuantity,
  })

  // refetch the purchased product and update the title model reflecting new quantity
  const fetchPurchasedProduct = useCallback(async () => {
    const purchasedProduct = await TitleService.getPurchasedProduct(title.uuid)
    if (purchasedProduct) updateTitle({ purchased_product: purchasedProduct })
  }, [title.uuid])

  const confirmPurchase = useCallback(async () => {
    setUiState(DialogState.SUBMITTED)
    clearErrors()

    let customerCard
    try {
      customerCard = await paymentFormRef.current?.onSubmitForm()
    } catch (error) {
      console.error(error)
      // Card validation/submit errors will be handled by global Error Emitter
      setUiState(DialogState.READY)
    }

    try {
      if (customerCard && title.product) {
        const [resp, error] = await CommerceService.purchaseProduct({
          card_id: customerCard.id,
          idempotency_key: idempotencyKey,
          product_uuid: title.product.uuid,
          quantity: purchaseQuantity,
          first_name: paymentFormRef.current?.first_name,
          last_name: paymentFormRef.current?.last_name,
        })

        // the purchase endpoint updates the user profile
        // update the session so subsequent purchase requests include the updated name
        if (resp?.profile) {
          session.updateProfile(resp.profile)
        }

        // errors from the purchaseProduct request may more verbose
        // so they appear in the error dialog
        if (error) {
          setDialogErrorCode(error)
          setUiState(DialogState.ERROR)
        } else if (resp?.transaction) {
          if (title.product) {
            GoogleTagManager.trackEcommerceEvent('purchase', {
              transaction_id: `${session.currentUser?.external_id}-${new Date().getTime()}`,
              value: price.priceInCents / 100.0,
              tax: price.taxInCents / 100.0,
              currency: price.currency,
              items: [
                {
                  item_id: title.product.uuid,
                  item_name: title.title,
                  price: (title.product.price ?? 0) / 100.0,
                  quantity: purchaseQuantity,
                },
              ],
            })
          }
          setUiState(DialogState.COMPLETED)
        }
      }
    } catch (error) {
      console.error(error)

      // other API errors that may have thrown
      //  will automatically be handled by APIErrorToast
      setUiState(DialogState.READY)
      await fetchPurchasedProduct()
    }
  }, [purchaseQuantity, idempotencyKey, title])

  const viewPurchasedScore = () => {
    window.location.assign(PageRoutes.purchasedScores())
  }

  const okButton = useMemo(() => {
    switch (uiState) {
      case DialogState.READY:
        return {
          action: confirmPurchase,
          text: t('action.confirmPurchase'),
          disabled: !isValid,
        }
      case DialogState.SUBMITTED:
        return {
          text: t('action.confirmPurchase'),
          disabled: true,
        }
      case DialogState.ERROR:
        return {
          action: () => null,
          disabled: true,
          text: t('action.confirmPurchase'),
        }
      case DialogState.COMPLETED:
        return {
          action: viewPurchasedScore,
          text: t('action.viewPurchasedScore'),
        }
      default:
        return {}
    }
  }, [uiState, isValid, confirmPurchase])

  const cancelButton = useMemo(() => {
    if (uiState === DialogState.COMPLETED) {
      return {
        action: () => {
          navigate(PageRoutes.musicSearchPath())
        },
        text: t('action.continueShopping'),
      }
    }
    return {
      action: () => {
        closeDialog('purchase_title_dialog')
      },
    }
  }, [uiState])

  const onErrorOk = useCallback(() => {
    // For most purchase errors, the error dialog's `Try Again` button should bring the user back to the payment form
    setUiState(DialogState.READY)

    // When the productNotIssued error happens, it is likely that the purchase was successful
    // but there was an issue with the DAM API, so rather than prompting them to purchase again,
    // close the dialog, which will attempt to fetch the purchased product (indicating to user that they now own the score)
    if (dialogErrorCode === 'errors.productNotIssued') {
      refetchPageData()
      refreshEmbed()
      closeDialog('purchase_title_dialog')
    }
  }, [dialogErrorCode])

  useEffect(() => {
    switch (uiState) {
      case DialogState.READY:
        setIdempotencyKey(CommerceService.generateIdempotencyKey())
        break
      case DialogState.ERROR:
        openDialog('purchase_title_error')
        break
      case DialogState.COMPLETED:
        openDialog('purchase_title_complete', { closeOthers: true })
        refetchPageData() // update the ui to show user owns this title
        refreshEmbed()
        break
      default:
        closeDialog('purchase_title_error')
    }
  }, [uiState])

  // Send Begin Checkout event to Google Analytics
  useEffect(() => {
    if (!title.product) return

    GoogleTagManager.trackEcommerceEvent('begin_checkout', {
      value: price.getPrice(),
      tax: price.taxInCents / 100.0,
      currency: price.currency,
      items: [
        {
          item_id: title.product.uuid,
          item_name: title.title,
          price: (title.product.price ?? 0) / 100.0,
          quantity: purchaseQuantity,
        },
      ],
    })
  }, [])

  return {
    uiState,
    dialogErrorCode,
    okButton,
    cancelButton,
    paymentFormRef,
    selectedAuthorizedPerformers,
    setSelectedAuthorizedPerformers,
    setIsValid,
    setTaxApiParams,
    setUiState,
    onErrorOk,
    price,
    clientSecret,
  }
}
export default usePurchaseTitleDialog
